/**
 * Links Doc Reviewer (RACI) rows to doc-transmittal `number` by matching
 * transmittal document docNum/revNum (see Table/README.md — DocTransmittalDocument).
 */

const normalizeDocRevKey = (docNum, revNum) =>
  `${String(docNum ?? "").trim().toLowerCase()}|${String(revNum ?? "").trim().toLowerCase()}`;

/**
 * @param {Array<{ number?: string, documents?: Array<{ docNum?: string, revNum?: string }> }>} transmittals
 * @returns {Map<string, string>}
 */
export function buildDocTransmittalNumberLookup(transmittals) {
  const lookup = new Map();
  if (!Array.isArray(transmittals)) return lookup;

  transmittals.forEach((transmittal) => {
    const number = transmittal?.number;
    if (!number) return;

    (transmittal.documents || []).forEach((doc) => {
      const key = normalizeDocRevKey(doc?.docNum, doc?.revNum);
      if (!key || key === "|") return;

      const existing = lookup.get(key);
      if (!existing) {
        lookup.set(key, number);
      } else if (existing !== number && !existing.includes(number)) {
        lookup.set(key, `${existing}, ${number}`);
      }
    });
  });

  return lookup;
}

/**
 * @param {Array<Record<string, unknown>>} racis
 * @param {Map<string, string>} lookup
 */
export function enrichRacisWithTransmittalNumbers(racis, lookup) {
  if (!lookup?.size || !Array.isArray(racis)) return racis || [];

  return racis.map((raci) => {
    const existing =
      raci.docTransmittalNumber ??
      raci.docTransmittal?.number ??
      raci.transmittalNumber;
    if (existing) {
      return existing === raci.docTransmittalNumber
        ? raci
        : { ...raci, docTransmittalNumber: String(existing) };
    }

    const key = normalizeDocRevKey(raci.docNum, raci.revNum);
    const docTransmittalNumber = lookup.get(key);
    if (!docTransmittalNumber) return raci;

    return { ...raci, docTransmittalNumber };
  });
}
